import React, { useCallback, useState } from "react";
import {
  ActivityIndicator,
  Alert,
  Linking,
  Pressable,
  SafeAreaView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { router } from "expo-router";
import { CircleCheck, Map as MapIcon, MapPin, MapPinned } from "lucide-react-native";

import { useOnboarding } from "@/components/owner/OnboardingProvider";

import {
  captureBestShopPin,
  buildPinTitle,
  buildPinSubtitle,
  CapturedShopPin,
} from "@/lib/location/shopLocation";

export default function LocationPinScreen() {
  const { setLocationPinDraft } = useOnboarding();

  const [loading, setLoading] = useState(false);
  const [pin, setPin] = useState<CapturedShopPin | null>(null);
  const [showPinError, setShowPinError] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  /* ================================
     CAPTURE LOCATION
  ================================ */

  const handleCapture = useCallback(async () => {
    try {
      setLoading(true);
      setActionError(null);

      const result = await captureBestShopPin();
      setPin(result);
      setShowPinError(false);
    } catch (err: any) {
      const message = err?.message || "Unable to capture location.";
      setActionError(message);
      Alert.alert(
        "Location error",
        message
      );
    } finally {
      setLoading(false);
    }
  }, []);

  /* ================================
     SAVE LOCATION
  ================================ */

  const handleSave = useCallback(() => {
    if (!pin) {
      setShowPinError(true);
      setActionError("Capture and confirm your shop location before saving.");
      return;
    }

    setActionError(null);
    setLocationPinDraft({
      ...pin,
      confirmedByOwner: true,
    });

    router.back();
  }, [pin, setLocationPinDraft]);

  const handlePreviewMap = useCallback(async () => {
    if (!pin) {
      const message = "Capture location first.";
      setActionError(message);
      Alert.alert("No location", message);
      return;
    }

    setActionError(null);
    const url = `https://www.google.com/maps?q=${pin.lat},${pin.lng}`;

    try {
      const supported = await Linking.canOpenURL(url);

      if (!supported) {
        const message = "Could not open Google Maps.";
        setActionError(message);
        Alert.alert("Map unavailable", message);
        return;
      }

      await Linking.openURL(url);
    } catch {
      const message = "Could not open Google Maps.";
      setActionError(message);
      Alert.alert("Map unavailable", message);
    }
  }, [pin]);

  /* ================================
     DERIVED UI
  ================================ */

  const title = buildPinTitle(pin);
  const subtitle = buildPinSubtitle(pin);

  /* ================================
     RENDER
  ================================ */

  return (
    <SafeAreaView style={styles.container}>
      {/* ================= CARD ================= */}
      {showPinError ? (
        <View style={styles.topErrorBox}>
          <Text style={styles.topErrorText}>
            Please fix the highlighted fields before continuing.
          </Text>
        </View>
      ) : null}

      {actionError ? (
        <View style={styles.topErrorBox}>
          <Text style={styles.topErrorText}>{actionError}</Text>
        </View>
      ) : null}

      <View style={[styles.card, showPinError && styles.cardError]}>
        <View style={styles.headerRow}>
          <MapPin size={22} color="#0f766e" />
          <Text style={styles.headerText}>Captured place</Text>
        </View>

        <Text style={styles.title}>{title}</Text>

        {!!subtitle && <Text style={styles.subtitle}>{subtitle}</Text>}

        {/* LANDMARK */}
        {!!pin?.landmarkName && (
          <View style={styles.infoBox}>
            <Text style={styles.infoLabel}>LANDMARK</Text>
            <Text style={styles.infoValue}>{pin.landmarkName}</Text>
          </View>
        )}

        {/* ROAD */}
        {!!pin?.roadName && (
          <View style={styles.infoBox}>
            <Text style={styles.infoLabel}>ROAD</Text>
            <Text style={styles.infoValue}>{pin.roadName}</Text>
          </View>
        )}

        {/* AREA */}
        {!!pin?.areaName && (
          <View style={styles.infoBox}>
            <Text style={styles.infoLabel}>AREA</Text>
            <Text style={styles.infoValue}>{pin.areaName}</Text>
          </View>
        )}

        {/* ACCURACY (optional, subtle) */}
        {!!pin?.accuracyMeters && (
          <View style={styles.metaRow}>
            <Text style={styles.metaText}>
              Accuracy ±{pin.accuracyMeters}m
            </Text>
          </View>
        )}

        {/* EMPTY STATE */}
        {!pin && (
          <Text style={styles.emptyText}>
            Capture your shop location to help customers find you easily.
          </Text>
        )}

        {showPinError ? (
          <Text style={styles.errorText}>
            Capture and confirm your shop location before saving.
          </Text>
        ) : null}
      </View>

      {/* ================= BUTTONS ================= */}

      <Pressable
        style={[styles.primaryBtn, loading && styles.disabledBtn]}
        onPress={handleCapture}
        disabled={loading}
      >
        {loading ? (
          <ActivityIndicator color="#fff" />
        ) : (
          <>
            <MapPinned size={20} color="#fff" style={styles.buttonIcon} />
            <Text style={styles.primaryBtnText}>Capture location</Text>
          </>
        )}
      </Pressable>

      <Pressable
        style={styles.secondaryBtn}
        onPress={handlePreviewMap}
      >
        <MapIcon size={20} color="#1e293b" style={styles.buttonIcon} />
        <Text style={styles.secondaryBtnText}>Preview map</Text>
      </Pressable>

      <Pressable
        style={[
          styles.saveBtn,
          loading && styles.disabledBtn,
        ]}
        onPress={handleSave}
        disabled={loading}
      >
        <CircleCheck size={20} color="#fff" style={styles.buttonIcon} />
        <Text style={styles.saveBtnText}>Confirm and save</Text>
      </Pressable>
    </SafeAreaView>
  );
}

/* ================================
   STYLES
================================ */

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
    backgroundColor: "#f8fafc",
  },

  card: {
    backgroundColor: "#fff",
    borderRadius: 16,
    padding: 16,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: "#fff",
  },

  cardError: {
    borderColor: "#ef4444",
  },

  topErrorBox: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: "#fca5a5",
    backgroundColor: "#fef2f2",
    padding: 12,
    marginBottom: 12,
  },

  topErrorText: {
    color: "#991b1b",
    fontSize: 13,
    lineHeight: 18,
    fontWeight: "700",
  },

  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
    gap: 8,
  },

  headerText: {
    fontSize: 14,
    color: "#64748b",
  },

  title: {
    fontSize: 22,
    fontWeight: "700",
    color: "#0f172a",
    marginBottom: 4,
  },

  subtitle: {
    fontSize: 14,
    color: "#475569",
    marginBottom: 12,
  },

  infoBox: {
    backgroundColor: "#f1f5f9",
    padding: 12,
    borderRadius: 10,
    marginTop: 10,
  },

  infoLabel: {
    fontSize: 11,
    color: "#64748b",
    marginBottom: 4,
  },

  infoValue: {
    fontSize: 14,
    color: "#0f172a",
    fontWeight: "600",
  },

  metaRow: {
    marginTop: 12,
  },

  metaText: {
    fontSize: 12,
    color: "#94a3b8",
  },

  emptyText: {
    marginTop: 12,
    fontSize: 13,
    color: "#64748b",
  },

  errorText: {
    marginTop: 10,
    color: "#dc2626",
    fontSize: 12,
    lineHeight: 17,
    fontWeight: "700",
  },

  buttonIcon: {
    marginRight: 8,
  },

  primaryBtn: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#0f766e",
    padding: 14,
    borderRadius: 12,
    marginBottom: 10,
  },

  primaryBtnText: {
    color: "#fff",
    fontWeight: "600",
  },

  secondaryBtn: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#cbd5f5",
    padding: 14,
    borderRadius: 12,
    marginBottom: 10,
  },

  secondaryBtnText: {
    color: "#1e293b",
    fontWeight: "500",
  },

  saveBtn: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#2563eb",
    padding: 14,
    borderRadius: 12,
  },

  saveBtnText: {
    color: "#fff",
    fontWeight: "600",
  },

  disabledBtn: {
    opacity: 0.5,
  },
});
